import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface Submission {
    id: string;
    organization_id: string;
    generator_id: string;
    payload: Record<string, unknown>;
    contact_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    estimated_total: number | null;
    status: string;
    source: string | null;
    created_at: string;
}

export type SubmissionStatus = 'new' | 'contacted' | 'quoted' | 'converted' | 'lost';
export const STATUS_VALUES: SubmissionStatus[] = ['new', 'contacted', 'quoted', 'converted', 'lost'];
export const STATUS_LABELS: Record<SubmissionStatus, string> = {
    new: 'Nouveau',
    contacted: 'Contacté',
    quoted: 'Devis envoyé',
    converted: 'Client',
    lost: 'Perdu',
};

interface UseSubmissionsArgs {
    organizationId: string | null | undefined;
    generatorId?: string | null;
}

interface UseSubmissionsResult {
    submissions: Submission[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    updateStatus: (id: string, status: SubmissionStatus) => Promise<void>;
    countsByGenerator: Record<string, number>;
}

export function useSubmissions({ organizationId, generatorId }: UseSubmissionsArgs): UseSubmissionsResult {
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSubmissions = useCallback(async () => {
        if (!organizationId) {
            setSubmissions([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);

        let query = supabase
            .from('submissions')
            .select('*')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (generatorId) query = query.eq('generator_id', generatorId);

        const { data, error: err } = await query;
        if (err) {
            setError(err.message);
            setLoading(false);
            return;
        }
        setSubmissions((data ?? []) as Submission[]);
        setLoading(false);
    }, [organizationId, generatorId]);

    useEffect(() => {
        void fetchSubmissions();
    }, [fetchSubmissions]);

    const updateStatus = useCallback(async (id: string, status: SubmissionStatus) => {
        const { error: err } = await supabase
            .from('submissions')
            .update({ status })
            .eq('id', id);
        if (err) {
            setError(err.message);
            return;
        }
        setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
    }, []);

    const countsByGenerator = useMemo(() => {
        const map: Record<string, number> = {};
        for (const s of submissions) map[s.generator_id] = (map[s.generator_id] ?? 0) + 1;
        return map;
    }, [submissions]);

    return { submissions, loading, error, refetch: fetchSubmissions, updateStatus, countsByGenerator };
}
